import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Avatar,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Hotel,
  PersonAdd,
  LocalHospital,
  Warning,
  CheckCircle,
  SwapHoriz,
  ExitToApp,
  Person,
  TrendingUp,
  Close,
  Search,
  AutoAwesome,
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { loadPendingPatientsdb ,loadWards_bed ,loadBedsByWarddb ,assignBedToPatient ,loadOccupancyStatsdb ,nurseDischargeBeddb ,patientsByWarddb, getNurseAssignedWardsdb } from '../../BaseURL';
import Swal from 'sweetalert2';
import moment from "moment";
import AIDischargeNoteDialog from '../../Common/AIDischargeNoteDialog';

export default function WardDashboard() {
  const muiTheme = useTheme();
  const isDark   = muiTheme.palette.mode === 'dark';
  const PAGE_BG    = muiTheme.palette.background.default;
  const PAPER_BG   = muiTheme.palette.background.paper;
  const SOFT_BG    = isDark ? '#0E0E14' : '#f8fafc';
  const TITLE_COL  = muiTheme.palette.text.primary;
  const MUTED_COL  = muiTheme.palette.text.secondary;
  const BORDER_COL = muiTheme.palette.divider;

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);


  const [pendingPatients, setPendingPatients] = useState([]);
  const [selectedWardPatients, setSelectedWardPatients] = useState([]);
  const [activeWardId, setActiveWardId] = useState(null);
  const [openWardDialog, setOpenWardDialog] = useState(false);


const loadPatientsByWard = async (wardId) => {
  try {
    const res = await axios.get(
      `${patientsByWarddb}?wardId=${wardId}`
    );

    if (res.data.success) {
      setSelectedWardPatients(res.data.patients);
      setActiveWardId(wardId);
      setOpenWardDialog(true); // ✅ OPEN DIALOG
    }
  } catch (err) {
    console.error("Failed to load ward patients", err);
    Swal.fire("Error", "Failed to load ward patients", "error");
  }
};


  useEffect(() => {
    loadPendingPatients();
     loadWards();
     loadStats()
     
  }, []);


  const loadPendingPatients = async () => {
    try {
      // Both ER and regular Wards see the full pending queue. Whoever
      // assigns the bed dictates the final status — ER side stamps
      // status='Emergency' on the patient, regular ward side preserves
      // Admission/Detention (and flips Emergency → Admission). This keeps
      // doctor mistakes recoverable from either panel.
      const res = await axios.get(loadPendingPatientsdb);

      if (res.data.success) {
        setPendingPatients(res.data.pendingPatients);
        console.log(res.data.pendingPatients)
      }
    } catch (err) {
      console.error("Failed to load pending patients");
    }
  };


const loadBedsByWard = async (wardId) => {
  try {
    const res = await axios.get(`${loadBedsByWarddb}?wardId=${wardId}`);

    setBedsList(res.data.beds); 
  } catch (err) {
    console.error("Failed to load beds", err);
  }
};


const [stats, setStats] = useState(null);
const [occupancyData, setOccupancyData] = useState([]);
const [pieData, setPieData] = useState([]);


const loadStats = async () => {
  try {
    const res = await axios.get(loadOccupancyStatsdb);

    if (res.data.success) {
      setStats(res.data.stats);
      setOccupancyData(res.data.trend);
      setPieData(res.data.bedDistribution);
    }
  } catch (err) {
    console.error("Failed to load occupancy stats", err);
  }
};


  const dischargeReasons = [
    '1. Clinical Recovery',
    '2. Improved Condition',
    '3. Transfer to Another Facility',
    '4. Discharge Against Medical Advice (DAMA)',
    '5. Financial or Administrative Reasons',
    '6. Death',
    '7. No Improvement / Non-Responsive to Treatment',
    '8. Home-Based Follow-Up Plan',
    '9. Social or Personal Reasons'
  ];

  const [dischargeReason, setDischargeReason] = useState('');
  const [dischargeNote, setDischargeNote] = useState('');
  const [aiDischargeOpen, setAiDischargeOpen] = useState(false);

  

  // Visual style by action type — Admit / Detain / Discharge each get their own chip
  const getActionColor = (action) => {
    if (action === 'Admit')  return 'primary';
    if (action === 'Detain') return 'warning';
    return 'secondary'; // Discharge
  };

  const getActionIcon = (action) => {
    if (action === 'Admit')  return '🛏️';
    if (action === 'Detain') return '⏱️';
    return '🚪';
  };

  const getActionLabel = (action) => {
    if (action === 'Detain') return 'Detain (24h)';
    return action;
  };

  const getInitials = (name) => {
    if (!name) return "";
    if (typeof name !== "string") return "";

    const parts = name.trim().split(" ");
    if (parts.length === 0) return "";

    let initials = parts[0][0] || "";
    if (parts.length > 1) {
      initials += parts[1][0] || "";
    }

    return initials.toUpperCase();
  };

const safe = (v) => (v ? v.toString().toLowerCase() : "");

const filteredPatients = useMemo(() => {
  const q = searchQuery.toLowerCase().trim();

  if (!q) return pendingPatients;

  return pendingPatients.filter((p) =>
    safe(p.name).includes(q) || safe(p.id).includes(q)
  );
}, [pendingPatients, searchQuery]);


const paginatedPatients = useMemo(() => {
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  return filteredPatients.slice(startIndex, endIndex);
}, [filteredPatients, page, rowsPerPage]);



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const COLORS = ['#f44336', '#4caf50', '#ff9800', '#2196f3'];



    const [wardsList, setWardsList] = useState([]);
    const [bedsList, setBedsList] = useState([]);
    const [selectedWardId, setSelectedWardId] = useState(null);
    const [selectedBed, setSelectedBed] = useState(null);


    const loadWards = async () => {
  try {
    const res = await axios.get(loadWards_bed); // your backend URL
    const allWards = res.data.wards || [];

    // Scope wards to the logged-in nurse's assignments.
    // Same rule as InPatient: admins see everything; nurses with no
    // assignments also see everything (default); nurses with assignments
    // see only those wards.
    const savedUser = localStorage.getItem('userInfo');
    const user = savedUser ? JSON.parse(savedUser) : null;
    const isAdmin = user?.role === 'System Administrator'
                 || user?.role === 'Head Of Assembly'
                 || user?.role === 'Head Of Department'
                 || user?.role === 'Doctor';
    const staffCode = user?.trans_code;

    if (!isAdmin && staffCode) {
      try {
        const wardRes = await axios.get(getNurseAssignedWardsdb, {
          params: { staffCode },
        });
        if (wardRes.data?.success && wardRes.data.results.length > 0) {
          const allowedIds = new Set(wardRes.data.results.map(w => Number(w.ward_id)));
          setWardsList(allWards.filter(w => allowedIds.has(Number(w.id))));
          return;
        }
      } catch {
        // fall through → show all wards if the lookup fails
      }
    }

    setWardsList(allWards);
  } catch (err) {
    console.error("Failed to load wards", err);
  }
};


const assignBed = async () => {
  if (!selectedBed || !selectedWardId || !selectedPatient) {
    Swal.fire("Error", "Please select a ward and bed", "error");
    return;
  }

  const wardName  = wardsList.find(w => w.id === selectedWardId)?.name || '—';
  const initials  = (selectedPatient.name || '?').split(/\s+/).filter(Boolean).slice(0,2).map(s=>s[0]).join('').toUpperCase();
  const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
  const fromState = selectedPatient.state || 'Admission';
  const conversionLine = fromState !== 'Emergency'
    ? `<div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#FEF3C7;color:#92400E;font-weight:700;font-size:0.85rem;">
         ⚠ Doctor flagged this patient as <strong>${escapeHtml(fromState)}</strong> — assigning a bed on the ER panel will <u>convert</u> the status to <strong>EMERGENCY</strong>.
       </div>`
    : '';

  const result = await Swal.fire({
    title: 'Confirm ER Bed Assignment',
    html: `
      <div style="text-align:left;font-family:Inter,Segoe UI,sans-serif;">
        <div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e5e9ef;border-radius:10px;background:#f8fafc;">
          <div style="width:48px;height:48px;border-radius:50%;background:#dc2626;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.05rem;">${initials}</div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;color:#0f172a;font-size:1rem;line-height:1.2;">${escapeHtml(selectedPatient.name)}</div>
            <div style="color:#64748b;font-size:0.8rem;margin-top:2px;">ID: <strong>${escapeHtml(selectedPatient.id)}</strong> · ${selectedPatient.age || '—'} yrs · ${escapeHtml(selectedPatient.gender)}</div>
            <div style="color:#64748b;font-size:0.78rem;margin-top:2px;">Insurance: ${escapeHtml(selectedPatient.plan)}</div>
            <div style="color:#64748b;font-size:0.78rem;">Doctor: ${escapeHtml(selectedPatient.doctor)}</div>
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px;">
          <div style="padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
            <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Ward</div>
            <div style="font-weight:700;color:#0f172a;margin-top:2px;">${escapeHtml(wardName)}</div>
          </div>
          <div style="padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
            <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Bed</div>
            <div style="font-weight:700;color:#0f172a;margin-top:2px;">${escapeHtml(selectedBed.bed_number)}${selectedBed.daily_rate ? ` <span style="color:#64748b;font-weight:500;font-size:0.8rem;">· GHS ${selectedBed.daily_rate}/day</span>` : ''}</div>
          </div>
        </div>

        <div style="margin-top:10px;padding:8px 12px;border-radius:8px;background:#FEE2E2;color:#991B1B;font-weight:700;font-size:0.85rem;">
          🚨 Final status will be <strong>EMERGENCY</strong> — patient appears on ER InPatient with the live timer.
        </div>
        ${conversionLine}

        <p style="margin-top:14px;color:#475569;font-size:0.82rem;">
          Once confirmed: bed marked <strong>occupied</strong>, patient becomes an <strong>inpatient</strong>, ER timer starts now.
        </p>
      </div>
    `,
    icon: undefined,
    showCancelButton: true,
    confirmButtonText: 'Yes, Assign Bed',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#64748b',
    width: 560,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  try {
    const res = await axios.post(assignBedToPatient, {
      patientId: selectedPatient.id,
      bedId: selectedBed.id,
      wardId: selectedWardId,
      dat: selectedPatient.dat,          // IMPORTANT
      patientName: selectedPatient.name, // used in backend
      isEmergency: true,                 // ER assignment → patient.status='Emergency'
    });

    Swal.fire("Success", "Bed assigned successfully!", "success");

    setSelectedPatient(null);
    setSelectedWardId(null);
    setSelectedBed(null);

    loadPendingPatients(); // refresh UI
  } catch (err) {
    console.error("Failed to assign bed", err);
    Swal.fire("Error", "Failed to assign bed", "error");
  }
};


const dischargePatient = async () => {
  if (!selectedPatient) {
    Swal.fire("Error", "No patient selected", "error");
    return;
  }

  if (!dischargeReason || !dischargeNote) {
    Swal.fire("Error", "Please fill in all discharge information", "error");
    return;
  }

  const dInitials  = (selectedPatient.name || '?').split(/\s+/).filter(Boolean).slice(0,2).map(s=>s[0]).join('').toUpperCase();
  const escapeHtml = (s) => String(s || '').replace(/[&<>"']/g, m => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));

  const result = await Swal.fire({
    title: 'Confirm Patient Discharge',
    html: `
      <div style="text-align:left;font-family:Inter,Segoe UI,sans-serif;">
        <div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e5e9ef;border-radius:10px;background:#f8fafc;">
          <div style="width:48px;height:48px;border-radius:50%;background:#dc2626;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:1.05rem;">${dInitials}</div>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;color:#0f172a;font-size:1rem;line-height:1.2;">${escapeHtml(selectedPatient.name)}</div>
            <div style="color:#64748b;font-size:0.8rem;margin-top:2px;">ID: <strong>${escapeHtml(selectedPatient.id)}</strong> · ${selectedPatient.age || '—'} yrs · ${escapeHtml(selectedPatient.gender)}</div>
          </div>
        </div>

        <div style="margin-top:10px;padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
          <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Reason for Discharge</div>
          <div style="color:#0f172a;font-weight:600;margin-top:2px;">${escapeHtml(dischargeReason)}</div>
        </div>

        <div style="margin-top:8px;padding:10px;border:1px solid #e5e9ef;border-radius:8px;background:#fff;">
          <div style="font-size:0.7rem;color:#94a3b8;font-weight:700;letter-spacing:0.6px;text-transform:uppercase;">Notes</div>
          <div style="color:#475569;font-size:0.85rem;margin-top:2px;white-space:pre-wrap;">${escapeHtml(dischargeNote)}</div>
        </div>

        <div style="margin-top:12px;padding:10px 12px;border-radius:8px;background:#FEE2E2;color:#991B1B;font-size:0.82rem;">
          <strong>This action cannot be undone from this screen.</strong> It will release the bed, mark the patient as discharged, finalise ward charges, and record the discharge notes.
        </div>
      </div>
    `,
    icon: undefined,
    showCancelButton: true,
    confirmButtonText: 'Yes, Discharge',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#64748b',
    width: 560,
    focusCancel: true,
  });

  if (!result.isConfirmed) return;

  try {
    const currentUser = localStorage.getItem('username') || 'System';

    const res = await axios.post(nurseDischargeBeddb, {
      patientId: selectedPatient.id,
      dischargedBy: currentUser,
      dischargeReason: dischargeReason,
      dischargeNote: dischargeNote
    });

    if (res.data.success) {
      Swal.fire({
        icon: "success",
        title: "Patient Discharged",
        text: res.data.message,
        confirmButtonColor: "#2e7d32"
      });

      // Reset states
      setSelectedPatient(null);
      setDischargeReason('');
      setDischargeNote('');

      // Refresh data
      loadPendingPatients();
      loadWards();
      loadStats();
    } else {
      Swal.fire("Error", res.data.message, "error");
    }
  } catch (err) {
    console.error("Failed to discharge patient", err);
    const data = err.response?.data;
    if (err.response?.status === 409 && data?.blocked && Array.isArray(data.incompleteDays)) {
      const minV = data.minVitalsPerDay ?? data.minPerDay ?? 3;
      const minN = data.minNotesPerDay  ?? 1;
      const rows = data.incompleteDays.map(d => {
        const need = [];
        if ((d.missingVitals ?? 0) > 0) need.push(`${d.missingVitals} more vital${d.missingVitals === 1 ? '' : 's'}`);
        if ((d.missingNotes  ?? 0) > 0) need.push(`${d.missingNotes} more note${d.missingNotes === 1 ? '' : 's'}`);
        return `
          <div style="display:flex;justify-content:space-between;gap:8px;padding:8px 10px;border:1px solid #fde68a;background:#fffbeb;border-radius:8px;margin-bottom:6px;">
            <div style="font-weight:700;color:#92400e;">${d.day || d.date || ''}</div>
            <div style="color:#92400e;font-size:0.85rem;">
              vitals: <strong>${d.vitals ?? 0}/${minV}</strong> · notes: <strong>${d.notes ?? 0}/${minN}</strong>
              ${need.length ? `<div style="font-size:0.78rem;color:#b45309;margin-top:2px;">Need: ${need.join(', ')}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      Swal.fire({
        icon: 'warning',
        title: 'Discharge Blocked — Compliance Not Met',
        html: `<p style="color:#475569;margin:0 0 8px;font-size:13px;">
             Every admission day must have at least <strong>${minV} vitals</strong> and <strong>${minN} nursing note${minN === 1 ? '' : 's'}</strong> before this patient can be discharged.
             Record the missing entries, then try again.
           </p>
           <div style="text-align:left;max-height:240px;overflow-y:auto;">${rows}</div>`,
        confirmButtonColor: '#b45309',
        width: 560,
      });
      return;
    }
    Swal.fire("Error", data?.message || "Failed to discharge patient", "error");
  }
};

if (!stats) return null;
  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', py: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Ward Management Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Real-time overview of all ward operations
          </Typography>
        </Box>

        {/* Quick Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Total Beds
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.totalBeds}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <Hotel sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Occupied
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.occupied}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <Person sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Available
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.available}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <CheckCircle sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                      Occupancy Rate
                    </Typography>
                    <Typography variant="h3" fontWeight={700}>
                      {stats.occupancyRate}%
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                    <TrendingUp sx={{ fontSize: 32 }} />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
          {/* Left Section - Pending Patients & Charts */}
          <Grid item xs={12} lg={8} size={9}>
            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
           
              <Grid item xs={12} md={8} size={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    7-Day Occupancy Trend
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={occupancyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip />
                      <Line type="monotone" dataKey="rate" stroke="#667eea" strokeWidth={3} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4} size={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Bed Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* Pending Patients Table */}
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 2.5,
                border: '1px solid #e5e9ef',
                bgcolor: PAPER_BG,
                overflow: 'hidden',
                fontFamily: '"Inter","Segoe UI",Arial,sans-serif',
              }}
            >
              {/* Header */}
              <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e5e9ef' }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Box>
                    <Typography sx={{
                      fontSize: '0.7rem', fontWeight: 700,
                      color: '#1A9B7F', letterSpacing: '1.4px',
                      textTransform: 'uppercase', mb: 0.25,
                    }}>
                      Pending Worklist
                    </Typography>
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                      Patients Awaiting Bed Assignment &amp; Discharge
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mt: 0.5 }}>
                      Showing <strong>{filteredPatients.length}</strong> of {pendingPatients.length} patient{pendingPatients.length === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label="🛏️ Admit"        sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: 'rgba(26,155,127,0.10)', color: '#0f4f3f' }} />
                    <Chip size="small" label="⏱️ Detain (24h)" sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: 'rgba(217,119,6,0.10)', color: '#92400e' }} />
                    <Chip size="small" label="🚪 Discharge"    sx={{ height: 24, fontWeight: 600, fontSize: '0.72rem', bgcolor: 'rgba(67,56,202,0.10)', color: '#3730a3' }} />
                  </Stack>
                </Stack>
              </Box>

              {/* Search Bar */}
              <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #e5e9ef', bgcolor: '#fafbfc' }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by patient name or ID…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    maxWidth: 420,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.75,
                      bgcolor: PAPER_BG,
                      fontSize: '0.88rem',
                      '& fieldset': { borderColor: '#e5e9ef' },
                      '&:hover fieldset': { borderColor: '#1A9B7F' },
                      '&.Mui-focused fieldset': { borderColor: '#1A9B7F', borderWidth: 1.5 },
                      '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(26,155,127,0.10)' },
                    },
                  }}
                />
              </Box>

              {/* Table */}
              <TableContainer>
                <Table size="small" sx={{ minWidth: 880 }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: SOFT_BG, borderBottom: `1px solid ${BORDER_COL}` } }}>
                      {[
                        { label: 'Patient',     w: '24%' },
                        { label: 'Age / Gender',w: '11%' },
                        { label: 'Plan',        w: '20%' },
                        { label: 'Type',        w: '12%' },
                        { label: 'Wait',        w: '10%' },
                        { label: 'Doctor',      w: '12%' },
                        { label: '',            w: '11%', align: 'right' },
                      ].map((c) => (
                        <TableCell
                          key={c.label || 'actions'}
                          align={c.align || 'left'}
                          sx={{
                            width: c.w,
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            color: '#475569',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            py: 1.25,
                          }}
                        >
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedPatients.length > 0 ? (
                      paginatedPatients.map((patient) => {
                        const isDetain    = patient.action === 'Detain';
                        const isAdmit     = patient.action === 'Admit';
                        const isDischarge = patient.action === 'Discharge';
                        const accent =
                          isDetain    ? { bg: 'rgba(217,119,6,0.10)',  text: '#92400e' } :
                          isDischarge ? { bg: 'rgba(67,56,202,0.10)',  text: '#3730a3' } :
                                        { bg: 'rgba(26,155,127,0.10)', text: '#0f4f3f' };

                        return (
                          <TableRow
                            key={patient.trans_code || patient.id}
                            hover
                            sx={{
                              '& td': { borderBottom: '1px solid #f1f5f9', py: 1.25 },
                              '&:last-child td': { borderBottom: 'none' },
                            }}
                          >
                            {/* Patient */}
                            <TableCell>
                              <Stack direction="row" spacing={1.25} alignItems="center">
                                <Avatar sx={{
                                  width: 36, height: 36,
                                  bgcolor: accent.bg,
                                  color: accent.text,
                                  fontSize: 13, fontWeight: 700,
                                }}>
                                  {getInitials(patient.name)}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {patient.name || '—'}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
                                    ID: #{String(patient.id || '').padStart(4, '0')}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>

                            {/* Age / Gender */}
                            <TableCell>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#334155' }}>
                                {patient.age ?? '—'}<Box component="span" sx={{ color: '#94a3b8', fontWeight: 400, ml: 0.5 }}>yrs</Box>
                              </Typography>
                              <Chip
                                label={patient.gender || '—'}
                                size="small"
                                sx={{
                                  mt: 0.25, height: 18, fontSize: '0.65rem', fontWeight: 700,
                                  bgcolor: patient.gender === 'MALE' ? '#EFF6FF' : '#FDF2F8',
                                  color:   patient.gender === 'MALE' ? '#1D4ED8' : '#BE185D',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            </TableCell>

                            {/* Plan */}
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: '0.82rem', color: '#334155',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.4,
                                }}
                                title={patient.plan || ''}
                              >
                                {patient.plan || '—'}
                              </Typography>
                            </TableCell>

                            {/* Type */}
                            <TableCell>
                              <Chip
                                label={`${getActionIcon(patient.action)} ${getActionLabel(patient.action) || '—'}`}
                                size="small"
                                sx={{
                                  height: 22, fontSize: '0.7rem', fontWeight: 700,
                                  bgcolor: accent.bg, color: accent.text,
                                  '& .MuiChip-label': { px: 1 },
                                }}
                              />
                            </TableCell>

                            {/* Wait */}
                            <TableCell>
                              <Typography sx={{ fontSize: '0.78rem', color: '#475569', fontWeight: 500 }}>
                                ⏱️ {patient.waitTime || '—'}
                              </Typography>
                            </TableCell>

                            {/* Doctor */}
                            <TableCell>
                              <Typography sx={{ fontSize: '0.8rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {patient.doctor || '—'}
                              </Typography>
                            </TableCell>

                            {/* Action button */}
                            <TableCell align="right">
                              {(isAdmit || isDetain) ? (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<Hotel sx={{ fontSize: 15 }} />}
                                  onClick={() => setSelectedPatient(patient)}
                                  sx={{
                                    textTransform: 'none', fontWeight: 600,
                                    boxShadow: 'none',
                                    bgcolor: isDetain ? '#d97706' : '#1A9B7F',
                                    '&:hover': {
                                      bgcolor: isDetain ? '#b45309' : '#157763',
                                      boxShadow: isDetain
                                        ? '0 4px 12px rgba(217,119,6,0.25)'
                                        : '0 4px 12px rgba(26,155,127,0.25)',
                                    },
                                  }}
                                >
                                  Assign Bed
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<ExitToApp sx={{ fontSize: 15 }} />}
                                  onClick={() => setSelectedPatient(patient)}
                                  sx={{
                                    textTransform: 'none', fontWeight: 600,
                                    borderColor: '#4338ca', color: '#4338ca',
                                    '&:hover': { borderColor: '#3730a3', bgcolor: 'rgba(67,56,202,0.06)' },
                                  }}
                                >
                                  Discharge
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                          <PersonAdd sx={{ fontSize: 36, color: '#cbd5e1', mb: 1 }} />
                          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
                            {searchQuery
                              ? `No patients match "${searchQuery}"`
                              : 'No patients pending bed assignment or discharge.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredPatients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid #e5e9ef',
                  '& .MuiTablePagination-toolbar': { minHeight: 48 },
                }}
              />
            </Paper>
          </Grid>

          {/* Right Sidebar */}
          <Grid item xs={12} lg={4} size={3}>
            {/* Today's Summary */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Today's Summary
              </Typography>
              <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'primary.light', width: 48, height: 48 }}>
                    <PersonAdd />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Admissions Today
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {stats.todayAdmissions}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: 'error.light', width: 48, height: 48 }}>
                    <ExitToApp />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Expected Discharges
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                      {stats.expectedDischarges}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>

            {/* Ward Overview */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Ward Overview
              </Typography>
              <Stack spacing={3}>
               {wardsList.map((ward) => (
                <Box
                  key={ward.id}
                  sx={{ cursor: "pointer" }}
                  onClick={() => loadPatientsByWard(ward.id)}
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={activeWardId === ward.id ? 700 : 500}
                      color={activeWardId === ward.id ? "primary.main" : "text.primary"}
                    >
                      {ward.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ward.occupied}/{ward.capacity}
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={(ward.occupied / ward.capacity) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
              ))}


              </Stack>
            </Paper>

          

          </Grid>
        </Grid>

       
        <Dialog 
          open={Boolean(selectedPatient)} 
          onClose={() => {
            setSelectedPatient(null);
            setSelectedWardId(null);
            setSelectedBed(null);
            setDischargeReason('');
            setDischargeNote('');
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          {/* Header with colored background — different gradient per action type */}
          <DialogTitle
            sx={{
              background:
                selectedPatient?.action === 'Detain'
                  ? 'linear-gradient(135deg, #1A9B7F 0%, #157763 100%)'        // system teal for detention
                  : selectedPatient?.action === 'Admit'
                  ? 'linear-gradient(135deg, #1c7a24ff 0%, #23913eff 100%)'    // green for admission
                  : 'linear-gradient(135deg, #3ba53bff 0%, #23913eff 100%)',   // discharge
              color: 'white',
              pb: 3
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {selectedPatient?.action === 'Detain'
                    ? '⏱️ Assign Bed — Detention (24h)'
                    : selectedPatient?.action === 'Admit'
                    ? '🛏️ Assign Bed to Patient'
                    : '🚪 Process Patient Discharge'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.95 }}>
                  {selectedPatient?.action === 'Detain'
                    ? `Select an available bed for the detained patient ${selectedPatient?.name}. The 24-hour observation timer starts after bed assignment.`
                    : selectedPatient?.action === 'Admit'
                    ? `Select an available bed for ${selectedPatient?.name}`
                    : `Complete discharge process for ${selectedPatient?.name}`}
                </Typography>
              </Box>
              <IconButton 
                onClick={() => {
                  setSelectedPatient(null);
                  setSelectedWardId(null);
                  setSelectedBed(null);
                  setDischargeReason('');
                  setDischargeNote('');
                }}
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 4, pb: 3 }}>
            {selectedPatient && (
              <>
                {/* Patient Information Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 4,
                    mt: 2,
                    border: '2px solid',
                    borderColor:
                      selectedPatient.action === 'Detain' ? 'primary.main'
                      : selectedPatient.action === 'Admit' ? 'primary.main'
                      : 'secondary.main',
                    borderRadius: 2,
                    bgcolor:
                      selectedPatient.action === 'Detain' ? 'primary.50'
                      : selectedPatient.action === 'Admit' ? 'primary.50'
                      : 'secondary.50'
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    📋 Patient Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Patient Name
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.name}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Age / Gender
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.age} years / {selectedPatient.gender}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Plan
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.plan}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Action Type
                        </Typography>
                        <Chip
                          label={`${getActionIcon(selectedPatient.action)} ${getActionLabel(selectedPatient.action)}`}
                          size="medium"
                          sx={{
                            fontWeight: 600,
                            // Detain uses the system teal in this dialog (not amber)
                            // so it matches the rest of the dialog chrome
                            ...(selectedPatient.action === 'Detain'
                              ? { bgcolor: 'rgba(26,155,127,0.10)', color: '#0f4f3f' }
                              : selectedPatient.action === 'Admit'
                              ? { bgcolor: 'rgba(26,155,127,0.10)', color: '#0f4f3f' }
                              : { bgcolor: 'rgba(67,56,202,0.10)',  color: '#3730a3' }),
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Wait Time
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          ⏱️ {selectedPatient.waitTime}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          Doctor
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {selectedPatient.doctor || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Conditional Content — bed-assignment (Admit OR Detain) vs Discharge */}
                {(selectedPatient.action === 'Admit' || selectedPatient.action === 'Detain') ? (
                  <Box>
                    {/* Ward Selection */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                        🏥 Step 1: Select Ward
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Ward *</InputLabel>
                        <Select
                          value={selectedWardId || ""}
                          label="Select Ward *"
                          onChange={(e) => {
                            setSelectedWardId(e.target.value);
                            loadBedsByWard(e.target.value);
                            setSelectedBed(null);
                          }}
                          sx={{ bgcolor: 'white' }}
                        >
                          {wardsList.map((ward) => (
                            <MenuItem key={ward.id} value={ward.id}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <LocalHospital fontSize="small" color="primary" />
                                <Typography>{ward.name}</Typography>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Bed Selection */}
                    {selectedWardId && (
                      <Box>
                        <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                          🛏️ Step 2: Select Available Bed
                        </Typography>
                        
                        {bedsList.length === 0 ? (
                          <Paper 
                            sx={{ 
                              p: 4, 
                              textAlign: 'center',
                              bgcolor: 'grey.50',
                              border: '2px dashed',
                              borderColor: 'grey.300'
                            }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              No available beds in this ward
                            </Typography>
                          </Paper>
                        ) : (
                          <Grid container spacing={2}>
                            {bedsList.map((bed) => (
                              <Grid item xs={6} sm={4} md={3} key={bed.id}>
                                <Paper
                                  onClick={() => setSelectedBed(bed)}
                                  elevation={selectedBed?.id === bed.id ? 8 : 2}
                                  sx={{
                                    p: 3,
                                    textAlign: "center",
                                    cursor: "pointer",
                                    bgcolor: selectedBed?.id === bed.id ? "success.main" : "white",
                                    color: selectedBed?.id === bed.id ? "white" : "text.primary",
                                    border: '2px solid',
                                    borderColor: selectedBed?.id === bed.id ? "success.dark" : "grey.200",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      transform: "translateY(-8px)",
                                      boxShadow: 6,
                                      borderColor: "success.main",
                                      bgcolor: selectedBed?.id === bed.id ? "success.dark" : "success.50"
                                    },
                                  }}
                                >
                                  <Hotel sx={{ fontSize: 40, mb: 1 }} />
                                  <Typography variant="h6" fontWeight={700}>
                                    {bed.bed_number}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    {selectedBed?.id === bed.id ? '✓ Selected' : 'Available'}
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom sx={{ mb: 3 }}>
                      📝 Discharge Information
                    </Typography>
                    <Stack spacing={3}>
                      <FormControl fullWidth>
                        <InputLabel>Reason for Discharge *</InputLabel>
                        <Select
                          value={dischargeReason}
                          label="Reason for Discharge *"
                          onChange={(e) => setDischargeReason(e.target.value)}
                          sx={{ bgcolor: 'white' }}
                        >
                          {dischargeReasons.map((reason, index) => (
                            <MenuItem key={index} value={reason}>
                              {reason}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.75 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AutoAwesome />}
                            onClick={() => setAiDischargeOpen(true)}
                            disabled={!selectedPatient?.id}
                            sx={{
                              textTransform: 'none', fontWeight: 700, fontSize: '0.74rem',
                              borderColor: '#6a11cb', color: '#6a11cb',
                              '&:hover': { borderColor: '#6a11cb', bgcolor: 'rgba(106,17,203,0.06)' },
                            }}
                          >
                            Generate with AI
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          label="Discharge Notes *"
                          multiline
                          rows={8}
                          value={dischargeNote}
                          onChange={(e) => setDischargeNote(e.target.value)}
                          placeholder="Enter detailed discharge notes including final diagnosis, treatment summary, medications, follow-up, and special care instructions."
                          helperText="Provide comprehensive discharge information for continuity of care"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Box>

                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 3, 
                          bgcolor: 'info.50',
                          border: '1px solid',
                          borderColor: 'info.200',
                          borderRadius: 2
                        }}
                      >
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle2" fontWeight={700} color="info.dark">
                            ℹ️ Important Discharge Checklist
                          </Typography>
                          {[
                            'Ensure all medications are prescribed and explained to patient/family',
                            'Schedule follow-up appointment before discharge',
                            'Provide written discharge instructions',
                            'Complete all pending billing procedures',
                            'Verify emergency contact information is current'
                          ].map((item, idx) => (
                            <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                              <CheckCircle sx={{ fontSize: 18, color: 'info.main', mt: 0.2 }} />
                              <Typography variant="body2" color="info.dark">
                                {item}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Paper>
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </DialogContent>

         <DialogActions sx={{ p: 3, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              onClick={() => {
                setSelectedPatient(null);
                setSelectedWardId(null);
                setSelectedBed(null);
                setDischargeReason('');
                setDischargeNote('');
              }}
              size="large"
              variant="outlined"
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>

            {(selectedPatient?.action === "Admit" || selectedPatient?.action === "Detain") ? (
              <Button
                variant="contained"
                size="large"
                disabled={!selectedBed}
                onClick={() => assignBed()}
                startIcon={<Hotel />}
                sx={{ minWidth: 200 }}
              >
                {selectedBed
                  ? (selectedPatient?.action === "Detain" ? 'Confirm Detention Bed' : 'Confirm Bed Assignment')
                  : 'Select a Bed First'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                disabled={!dischargeReason || !dischargeNote}
                onClick={() => dischargePatient()} // ✅ ADD THIS
                startIcon={<ExitToApp />}
                sx={{ minWidth: 200 }}
              >
                Complete Discharge
              </Button>
            )}
          </DialogActions>
        </Dialog>
        

      <Dialog
        open={openWardDialog}
        onClose={() => setOpenWardDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #389b38ff 0%, #3f922bff 100%)',
            color: 'white',
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Hotel sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Ward Patient List
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {selectedWardPatients.length} patient{selectedWardPatients.length !== 1 ? 's' : ''} currently admitted
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {selectedWardPatients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Hotel sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Patients in Ward
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This ward currently has no admitted patients
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      #
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Patient Information
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Age / Gender
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Bed Number
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                    >
                      Admission Date
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        py: 2
                      }}
                      align="center"
                    >
                      Days Spent
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {selectedWardPatients.map((p, index) => {
                    // Calculate days spent from admission date to now
                    const admissionDate = moment(p.admission_date || p.admitted_date);
                    const currentDate = moment();
                    const daysSpent = currentDate.diff(admissionDate, 'days');
                    
                    // Determine color based on length of stay
                    let daysColor = 'success.main';
                    if (daysSpent > 14) {
                      daysColor = 'error.main';
                    } else if (daysSpent > 7) {
                      daysColor = 'warning.main';
                    }

                    return (
                      <TableRow 
                        key={p.patient_id}
                        sx={{
                          '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                          '&:hover': { 
                            bgcolor: 'primary.lighter',
                            transition: 'all 0.2s ease'
                          }
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={index + 1}
                            size="small"
                            color="primary"
                            sx={{ 
                              fontWeight: 700,
                              minWidth: 32
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar 
                              sx={{ 
                                bgcolor: p.gender === 'MALE' ? 'info.main' : 'error.main',
                                width: 40,
                                height: 40,
                                fontWeight: 700
                              }}
                            >
                              {p.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>
                                {p.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {p.patientId}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" gap={0.5}>
                            <Chip
                              label={`${p.age} yrs`}
                              size="small"
                              sx={{ 
                                fontSize: '0.75rem',
                                height: 24
                              }}
                            />
                            <Chip
                              label={p.gender}
                              size="small"
                              color={p.gender === 'MALE' ? 'info' : 'error'}
                              sx={{ 
                                fontSize: '0.75rem',
                                height: 24,
                                fontWeight: 600
                              }}
                            />
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box 
                            sx={{ 
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: 'success.lighter',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'success.main'
                            }}
                          >
                            <Hotel sx={{ fontSize: 18, color: 'success.dark' }} />
                            <Typography 
                              variant="body2" 
                              fontWeight={700}
                              color="success.dark"
                            >
                              {p.bed_number}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={{ py: 2 }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {moment(p.admission_date || p.admitted_date).format('MMM DD, YYYY')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {moment(p.admission_date || p.admitted_date).format('hh:mm A')}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell align="center" sx={{ py: 2 }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <Box
                              sx={{
                                bgcolor: `${daysColor}15`,
                                border: '2px solid',
                                borderColor: daysColor,
                                borderRadius: '50%',
                                width: 56,
                                height: 56,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column'
                              }}
                            >
                              <Typography 
                                variant="h6" 
                                fontWeight={700}
                                sx={{ color: daysColor, lineHeight: 1 }}
                              >
                                {daysSpent}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: daysColor,
                                  fontSize: '0.65rem',
                                  fontWeight: 600
                                }}
                              >
                                {daysSpent === 1 ? 'day' : 'days'}
                              </Typography>
                            </Box>
                            
                            {daysSpent > 14 && (
                              <Chip
                                label="Long Stay"
                                size="small"
                                color="error"
                                sx={{ 
                                  fontSize: '0.65rem',
                                  height: 20,
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Summary Footer */}
          {selectedWardPatients.length > 0 && (
            <Box 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.50',
                borderTop: '2px solid',
                borderColor: 'divider'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: 'primary.lighter',
                      border: '1px solid',
                      borderColor: 'primary.main'
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} color="primary.dark">
                      {selectedWardPatients.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Total Patients
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: 'success.lighter',
                      border: '1px solid',
                      borderColor: 'success.main'
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} color="success.dark">
                      {selectedWardPatients.filter(p => {
                        const admissionDate = moment(p.admission_date || p.admitted_date);
                        const currentDate = moment();
                        const daysSpent = currentDate.diff(admissionDate, 'days');
                        return daysSpent <= 7;
                      }).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Recent Admissions (≤7 days)
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      textAlign: 'center',
                      bgcolor: 'error.lighter',
                      border: '1px solid',
                      borderColor: 'error.main'
                    }}
                  >
                    <Typography variant="h4" fontWeight={700} color="error.dark">
                      {selectedWardPatients.filter(p => {
                        const admissionDate = moment(p.admission_date || p.admitted_date);
                        const currentDate = moment();
                        const daysSpent = currentDate.diff(admissionDate, 'days');
                        return daysSpent > 14;
                      }).length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Long Stay (&gt;14 days)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Button
            onClick={() => setOpenWardDialog(false)}
            variant="contained"
            color="primary"
            startIcon={<Close />}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <AIDischargeNoteDialog
        open={aiDischargeOpen}
        onClose={() => setAiDischargeOpen(false)}
        patient={selectedPatient}
        dischargeReason={dischargeReason}
        onGenerated={(text) => setDischargeNote(text)}
      />

      </Container>
    </Box>
  );
}